import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Bed,
  BellRing,
  Building2,
  Check,
  Grid3X3,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneCall,
  PhoneForwarded,
  PhoneOff,
  Play,
  Radio,
  Shield,
  Utensils,
  Volume2,
  Wrench,
  X,
} from "lucide-react";
import { callsApi } from "@/api/calls/callsApi.js";
import {
  CANONICAL_DEPARTMENTS,
  HOTEL_DEPARTMENTS,
  departmentDisplayName,
  normalizeDepartment,
} from "@/utils/alerts/departmentAlerts.js";
import { CALL_STATES } from "../callConstants.js";

const cx = (...values) => values.flat().filter(Boolean).join(" ");

const formatTimer = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const getDepartmentIcon = (deptKey = "") => {
  const normalized = normalizeDepartment(deptKey);
  switch (normalized) {
    case HOTEL_DEPARTMENTS.housekeeping:
      return { icon: Bed, color: "text-purple-400", bg: "bg-purple-500/10" };
    case HOTEL_DEPARTMENTS.roomDining:
      return { icon: Utensils, color: "text-amber-400", bg: "bg-amber-500/10" };
    case HOTEL_DEPARTMENTS.maintenance:
      return { icon: Wrench, color: "text-orange-400", bg: "bg-orange-500/10" };
    case HOTEL_DEPARTMENTS.security:
      return { icon: Shield, color: "text-blue-400", bg: "bg-blue-500/10" };
    default:
      return { icon: Building2, color: "text-emerald-400", bg: "bg-emerald-500/10" };
  }
};

const CallButton = ({
  className,
  variant,
  ...props
}) => {
  const hasBgClass = className && /\bbg-/.test(className);

  return (
    <button
      className={cx(
        "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 disabled:pointer-events-none disabled:opacity-50 cursor-pointer touch-manipulation select-none relative z-20 active:scale-95",
        variant === "outline" && "border bg-white hover:bg-slate-100",
        variant === "destructive" && "bg-red-600 text-white hover:bg-red-700",
        !variant && !hasBgClass && "bg-primary text-primary-foreground hover:bg-primary/90",
        className,
      )}
      {...props}
    />
  );
};

const getInitials = (value = "Call") => {
  const words = String(value)
    .replace(/is calling/i, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return (words[0]?.[0] || "C").toUpperCase() + (words[1]?.[0] || "").toUpperCase();
};

const statusCopy = (callState, callInfo, isHeld, isRemoteHeld, isTransferring, transferringDepartment) => {
  const hasCallId = Boolean(callInfo?.callId);

  if (isTransferring || callInfo?.isTransferring) {
    const dept = (transferringDepartment || callInfo?.subtitle || "Department").toUpperCase();
    return {
      eyebrow: "Transferring call",
      status: `Transferring to ${dept}...`,
      detail: "Connecting to the department...",
    };
  }

  if (isHeld) {
    return {
      eyebrow: "Call on hold",
      status: "On hold (You)",
      detail: "Microphone & audio paused.",
    };
  }

  if (isRemoteHeld) {
    return {
      eyebrow: "Call on hold",
      status: "On hold (Remote)",
      detail: "The other party put the call on hold.",
    };
  }

  if (callState === CALL_STATES.queued) {
    const pos = callInfo?.queuePosition || 1;
    return {
      eyebrow: "Holding in queue",
      status: `You are #${pos} in line`,
      detail: callInfo?.subtitle || "All agents are currently on another line. Please hold...",
    };
  }

  if (callState === CALL_STATES.incoming) {
    return {
      eyebrow: "Incoming browser call",
      status: "Ringing on this device",
      detail: "Answer to start live audio.",
    };
  }

  if (callState === CALL_STATES.calling) {
    return {
      eyebrow: "Browser call",
      status: hasCallId ? "Call request sent. Waiting for answer..." : "Starting call...",
      detail: hasCallId
        ? "Waiting for the other device to answer."
        : "Preparing the call session.",
    };
  }

  if (callState === CALL_STATES.connecting) {
    return {
      eyebrow: "Connecting audio",
      status: "Answer received",
      detail: "Setting up live audio path.",
    };
  }

  if (callState === CALL_STATES.connected) {
    return {
      eyebrow: "Live browser call",
      status: "Connected",
      detail: "Audio is live.",
    };
  }

  return {
    eyebrow: "Browser call",
    status: "Call in progress",
    detail: callInfo?.subtitle || "",
  };
};

const stateIcon = (callState, isHeld, isRemoteHeld) => {
  if (isHeld || isRemoteHeld) return Pause;
  if (callState === CALL_STATES.queued) return Radio;
  if (callState === CALL_STATES.incoming) return BellRing;
  if (callState === CALL_STATES.connected) return Mic;
  if (callState === CALL_STATES.connecting) return Radio;
  return PhoneCall;
};

const CallPopup = ({
  callState,
  callInfo,
  waitingCall = null,
  heldCall = null,
  muted,
  isHeld = false,
  isRemoteHeld = false,
  isTransferring = false,
  transferringDepartment = null,
  callDuration = 0,
  onAccept,
  onHoldAndAnswerWaiting,
  onSwapActiveLine,
  onDismissWaitingCall,
  onDecline,
  onEnd,
  onToggleMute,
  onHoldCall,
  onTransferCall,
}) => {
  const [activeView, setActiveView] = useState("main"); // 'main' | 'transfer' | 'keypad'
  const [dialedDigits, setDialedDigits] = useState("");
  const [transferringTo, setTransferringTo] = useState(null);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [isLoadingDepts, setIsLoadingDepts] = useState(false);

  // Fetch callable departments when transfer menu is opened
  useEffect(() => {
    if (activeView === "transfer") {
      setIsLoadingDepts(true);
      callsApi.getCallableDepartments()
        .then((res) => {
          const list = res.data?.data || res.data?.Data || res.data || [];
          if (Array.isArray(list) && list.length > 0) {
            setDepartmentsList(list);
          } else {
            setDepartmentsList(
              CANONICAL_DEPARTMENTS.map((dept) => ({
                departmentKey: dept,
                departmentId: dept,
                departmentLabel: departmentDisplayName(dept),
              }))
            );
          }
        })
        .catch(() => {
          setDepartmentsList(
            CANONICAL_DEPARTMENTS.map((dept) => ({
              departmentKey: dept,
              departmentId: dept,
              departmentLabel: departmentDisplayName(dept),
            }))
          );
        })
        .finally(() => setIsLoadingDepts(false));
    }
  }, [activeView]);

  const isBusy = callState && callState !== CALL_STATES.idle;
  if (!isBusy) return null;

  const isIncoming = callState === CALL_STATES.incoming;
  const isCalling = callState === CALL_STATES.calling;
  const isConnecting = callState === CALL_STATES.connecting;
  const isConnected = callState === CALL_STATES.connected;
  const anyHeld = isHeld || isRemoteHeld;
  const copy = statusCopy(callState, callInfo, isHeld, isRemoteHeld, isTransferring, transferringDepartment);
  const Icon = stateIcon(callState, isHeld, isRemoteHeld);
  const title = callInfo?.title || "Live Call";
  const subtitle = isConnected ? (anyHeld ? copy.detail : callInfo?.subtitle || copy.detail) : callInfo?.subtitle || copy.detail;
  const targetCallId = callInfo?.callId || callInfo?.CallId;
  const closeAction = isIncoming ? onDecline : onEnd;

  const handleAccept = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onAccept?.(targetCallId);
  };

  const handleDecline = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onDecline?.(targetCallId);
  };

  const handleEnd = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onEnd?.(targetCallId);
  };

  const handleClose = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    closeAction?.(targetCallId);
  };

  const handleToggleHold = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onHoldCall?.(!isHeld);
  };

  const handleExecuteTransfer = async (dept) => {
    const targetDeptKey = dept.departmentKey || dept.departmentId || dept;
    const targetDeptLabel = dept.departmentLabel || departmentDisplayName(targetDeptKey);
    setTransferringTo(targetDeptLabel);
    try {
      if (onTransferCall) {
        await onTransferCall(targetDeptKey, targetDeptLabel);
      }
      setActiveView("main");
    } catch (e) {
      console.error("Failed to transfer call:", e);
    } finally {
      setTransferringTo(null);
    }
  };

  const handleKeyPress = (digit) => {
    setDialedDigits((prev) => prev + digit);
  };

  const popupContent = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-4 sm:p-6 overflow-y-auto select-none pointer-events-auto animate-in fade-in duration-200"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="m-auto w-full max-w-md overflow-hidden rounded-[28px] border border-white/20 bg-slate-900 shadow-2xl flex flex-col shrink-0 relative z-10 select-none pointer-events-auto animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* TRANSFER SUB-VIEW */}
        {activeView === "transfer" && (
          <div className="flex flex-col h-full min-h-[420px] bg-slate-900 text-white">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <button
                type="button"
                onClick={() => setActiveView("main")}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/20 transition cursor-pointer"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to call
              </button>
              <h3 className="font-bold text-sm tracking-wide">Transfer Call</h3>
              <div className="w-8" />
            </div>

            <div className="p-4 flex-1 overflow-y-auto space-y-2 max-h-[340px]">
              <p className="text-xs text-slate-400 mb-3 px-1">
                Select a department to transfer this active call to:
              </p>
              {isLoadingDepts ? (
                <div className="flex items-center justify-center py-12 text-slate-400">
                  <Radio className="h-6 w-6 animate-spin mr-2" />
                  Loading departments...
                </div>
              ) : (
                departmentsList.map((dept) => {
                  const deptKey = dept.departmentKey || dept.departmentId;
                  const deptLabel = dept.departmentLabel || departmentDisplayName(deptKey);
                  const iconInfo = getDepartmentIcon(deptKey);
                  const DeptIcon = iconInfo.icon;
                  const isCurrentTransfer = transferringTo === deptLabel;

                  return (
                    <button
                      key={deptKey}
                      type="button"
                      disabled={Boolean(transferringTo)}
                      onClick={() => handleExecuteTransfer(dept)}
                      className={cx(
                        "w-full flex items-center justify-between p-3.5 rounded-2xl border transition text-left cursor-pointer",
                        isCurrentTransfer
                          ? "bg-emerald-500/20 border-emerald-500/50"
                          : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cx("h-10 w-10 rounded-xl flex items-center justify-center", iconInfo.bg)}>
                          <DeptIcon className={cx("h-5 w-5", iconInfo.color)} />
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-white">{deptLabel}</p>
                          <p className="text-xs text-slate-400">Hotel desk</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/20">
                        {isCurrentTransfer ? "Transferring..." : "Transfer →"}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* KEYPAD SUB-VIEW */}
        {activeView === "keypad" && (
          <div className="flex flex-col h-full min-h-[420px] bg-slate-900 text-white">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <button
                type="button"
                onClick={() => setActiveView("main")}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/20 transition cursor-pointer"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to call
              </button>
              <h3 className="font-bold text-sm tracking-wide">Keypad</h3>
              <div className="w-8" />
            </div>

            <div className="p-5 flex flex-col items-center flex-1">
              <div className="h-12 w-full flex items-center justify-center text-2xl font-mono tracking-widest bg-white/5 rounded-2xl border border-white/10 mb-5 text-emerald-300">
                {dialedDigits || <span className="text-xs font-sans text-slate-400">Tap digits below</span>}
              </div>

              <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((digit) => (
                  <button
                    key={digit}
                    type="button"
                    onClick={() => handleKeyPress(digit)}
                    className="h-14 rounded-2xl bg-white/10 hover:bg-white/20 active:bg-white/30 text-white font-bold text-xl flex items-center justify-center transition cursor-pointer"
                  >
                    {digit}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MAIN CALL VIEW */}
        {activeView === "main" && (
          <>
            {/* MULTI-LINE ACTIVE HEADER */}
            {heldCall && (
              <div className="flex items-center justify-between px-4 py-2.5 bg-white/10 border-b border-white/10 text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                  <span className="font-semibold text-white truncate">Line 1: {title}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
                  <span className="text-amber-200/80 truncate max-w-[100px] sm:max-w-[140px]">
                    Line 2: {heldCall?.callerName || heldCall?.CallerName || "On Hold"}
                  </span>
                  <button
                    type="button"
                    onClick={onSwapActiveLine}
                    className="px-2.5 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-[11px] transition active:scale-95 cursor-pointer flex items-center gap-1"
                  >
                    Swap ⇄
                  </button>
                </div>
              </div>
            )}

            {/* CALL WAITING BANNER */}
            {waitingCall && (
              <div className="mx-3 mt-3 p-3.5 rounded-2xl bg-gradient-to-r from-amber-500/25 via-amber-600/20 to-orange-500/20 border border-amber-400/40 shadow-xl backdrop-blur-xl flex flex-col gap-3 animate-in slide-in-from-top-3 text-left">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                    </span>
                    <span className="text-[11px] font-extrabold text-amber-300 uppercase tracking-widest flex items-center gap-1.5">
                      <BellRing className="h-3 w-3 animate-bounce" /> Call Waiting
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={onDismissWaitingCall}
                    className="text-white/60 hover:text-white hover:bg-white/10 text-xs p-1 rounded-lg transition cursor-pointer"
                    title="Dismiss alert"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="flex items-center justify-between gap-3 bg-black/30 p-2.5 rounded-xl border border-white/5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-300 font-bold text-xs shrink-0">
                      {getInitials(waitingCall.callerName || waitingCall.CallerName || "Caller")}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white leading-tight truncate">
                        {waitingCall.callerName || waitingCall.CallerName || "Incoming Caller"}
                      </p>
                      <p className="text-[11px] text-amber-200/80 truncate mt-0.5">
                        {waitingCall.roomName || waitingCall.RoomName ? `Room ${waitingCall.roomName || waitingCall.RoomName}` : "Website Visitor"} • {waitingCall.targetDepartmentName || waitingCall.TargetDepartmentName || "Reception"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={onHoldAndAnswerWaiting}
                      className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-slate-950 font-extrabold text-xs shadow-lg shadow-emerald-500/20 transition active:scale-95 cursor-pointer flex items-center gap-1.5"
                    >
                      <PhoneCall className="h-3.5 w-3.5" />
                      Hold & Answer
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="relative bg-slate-900 px-5 py-6 text-white sm:px-8 sm:py-8 shrink flex flex-col items-center text-center select-none">
              <button
                type="button"
                onClick={handleClose}
                className="absolute right-4 top-4 rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white transition cursor-pointer touch-manipulation z-20"
                aria-label={isIncoming ? "Decline call" : "End call"}
              >
                <X className="h-5 w-5" />
              </button>

              {/* LIVE DURATION TIMER BADGE */}
              {isConnected && !anyHeld && (
                <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1 text-xs font-mono font-bold text-emerald-300">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  {formatTimer(callDuration)}
                </div>
              )}

              {/* AVATAR SECTION */}
              <div
                className={cx(
                  "relative mb-4 flex shrink-0 items-center justify-center rounded-full h-20 w-20 sm:h-24 sm:w-24 text-white transition-all",
                  anyHeld
                    ? "bg-amber-500/20 text-amber-300"
                    : isTransferring
                    ? "bg-blue-500/20 text-blue-300"
                    : "bg-white/10 text-white"
                )}
              >
                <span className={cx("absolute inset-0 rounded-full border", anyHeld ? "border-amber-400/40" : "border-white/20")} />
                <span
                  className={cx(
                    "absolute inset-[-10px] sm:inset-[-14px] rounded-full border animate-ping",
                    anyHeld ? "border-amber-400/20" : "border-white/15"
                  )}
                />
                {(isIncoming || isCalling || anyHeld || isTransferring) && (
                  <span
                    className={cx(
                      "absolute inset-[-20px] sm:inset-[-28px] rounded-full border animate-pulse",
                      anyHeld ? "border-amber-400/10" : "border-white/10"
                    )}
                  />
                )}
                <span className="text-2xl font-semibold sm:text-3xl">
                  {getInitials(title)}
                </span>
              </div>

              <p className={cx("text-xs font-semibold uppercase tracking-wider", anyHeld ? "text-amber-300" : isTransferring ? "text-blue-300" : "text-emerald-300")}>
                {copy.eyebrow}
              </p>
              <h2 className="mt-1 font-bold text-xl sm:text-2xl text-white leading-tight">
                {title}
              </h2>
              <p className="mt-1 max-w-xs text-xs sm:text-sm text-slate-300">
                {subtitle}
              </p>

              <div
                className={cx(
                  "mt-3.5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium ring-1 transition-all",
                  anyHeld
                    ? "bg-amber-500/20 text-amber-200 ring-amber-400/40"
                    : isTransferring
                    ? "bg-blue-500/20 text-blue-200 ring-blue-400/40"
                    : "bg-white/10 text-white ring-white/15"
                )}
              >
                <Icon className={cx("h-4 w-4", (isIncoming || isCalling || isConnecting || anyHeld || isTransferring) && "animate-pulse")} />
                {copy.status}
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="bg-slate-950 p-4 shrink-0 border-t border-white/10 relative z-20 pointer-events-auto select-none">
              {isIncoming ? (
                <div className="grid grid-cols-2 gap-3">
                  <CallButton
                    type="button"
                    onClick={handleAccept}
                    className="min-h-[48px] gap-2 rounded-2xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-bold text-base shadow-lg shadow-emerald-600/30 cursor-pointer touch-manipulation select-none"
                  >
                    <Phone className="h-5 w-5 text-white" />
                    Accept
                  </CallButton>
                  <CallButton
                    type="button"
                    onClick={handleDecline}
                    className="min-h-[48px] gap-2 rounded-2xl bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-bold text-base shadow-lg shadow-red-600/30 cursor-pointer touch-manipulation select-none"
                  >
                    <PhoneOff className="h-5 w-5 text-white" />
                    Decline
                  </CallButton>
                </div>
              ) : isConnected ? (
                <div className="space-y-3">
                  {/* IN-CALL CONTROLS 4-GRID (Mute, Hold, Transfer, Keypad) */}
                  <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
                    {/* MUTE / UNMUTE */}
                    <button
                      type="button"
                      onClick={onToggleMute}
                      className={cx(
                        "flex flex-col items-center justify-center p-2.5 rounded-2xl border transition cursor-pointer touch-manipulation",
                        muted
                          ? "bg-red-500/20 border-red-500/50 text-red-300"
                          : "bg-white/10 border-white/10 hover:bg-white/15 text-white"
                      )}
                    >
                      {muted ? <MicOff className="h-5 w-5 mb-1 text-red-400" /> : <Mic className="h-5 w-5 mb-1 text-white" />}
                      <span className="text-[11px] font-semibold">{muted ? "Unmute" : "Mute"}</span>
                    </button>

                    {/* HOLD / RESUME */}
                    <button
                      type="button"
                      onClick={handleToggleHold}
                      className={cx(
                        "flex flex-col items-center justify-center p-2.5 rounded-2xl border transition cursor-pointer touch-manipulation",
                        isHeld
                          ? "bg-amber-500/20 border-amber-500/50 text-amber-300"
                          : "bg-white/10 border-white/10 hover:bg-white/15 text-white"
                      )}
                    >
                      {isHeld ? <Play className="h-5 w-5 mb-1 text-amber-400" /> : <Pause className="h-5 w-5 mb-1 text-white" />}
                      <span className="text-[11px] font-semibold">{isHeld ? "Resume" : "Hold"}</span>
                    </button>

                    {/* TRANSFER */}
                    <button
                      type="button"
                      onClick={() => setActiveView("transfer")}
                      className="flex flex-col items-center justify-center p-2.5 rounded-2xl bg-white/10 border border-white/10 hover:bg-white/15 text-white transition cursor-pointer touch-manipulation"
                    >
                      <PhoneForwarded className="h-5 w-5 mb-1 text-sky-400" />
                      <span className="text-[11px] font-semibold">Transfer</span>
                    </button>

                    {/* KEYPAD */}
                    <button
                      type="button"
                      onClick={() => setActiveView("keypad")}
                      className="flex flex-col items-center justify-center p-2.5 rounded-2xl bg-white/10 border border-white/10 hover:bg-white/15 text-white transition cursor-pointer touch-manipulation"
                    >
                      <Grid3X3 className="h-5 w-5 mb-1 text-indigo-400" />
                      <span className="text-[11px] font-semibold">Keypad</span>
                    </button>
                  </div>

                  {/* END CALL BUTTON */}
                  <CallButton
                    type="button"
                    onClick={handleEnd}
                    className="w-full min-h-[48px] gap-2 rounded-2xl bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-bold text-base shadow-lg shadow-red-600/30 cursor-pointer touch-manipulation select-none"
                  >
                    <PhoneOff className="h-5 w-5 text-white" />
                    End Call
                  </CallButton>
                </div>
              ) : (
                <CallButton
                  type="button"
                  onClick={handleEnd}
                  className="w-full min-h-[48px] gap-2 rounded-2xl bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-bold text-base shadow-lg shadow-red-600/30 cursor-pointer touch-manipulation select-none"
                >
                  <PhoneOff className="h-5 w-5 text-white" />
                  Cancel Call
                </CallButton>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(popupContent, document.body) : popupContent;
};

export { CallPopup };
export default CallPopup;
